/* eslint-disable unicorn/no-null */
import type { FC } from 'react';
import { useState } from 'react';

import { Box, Button, Divider, FileInput, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import {
    useExportCustomerProjectsMutation,
    useImportCustomerProjectsMutation
} from '@/modules/SettingsDrawer/CustomerImportExport.queries';

const customerImportFileInputProps = {
    name: 'customer-projects-import-file',
    'data-testid': 'customer-projects-import-file'
};

const CustomerImportExport: FC = () => {
    const [file, setFile] = useState<File | null>(null);

    const exportMutation = useExportCustomerProjectsMutation();
    const importMutation = useImportCustomerProjectsMutation();
    const [isBackingUp, backupDisclosure] = useDisclosure(false);
    const isImportInProgress = isBackingUp || importMutation.isPending;
    const isOperationInProgress =
        exportMutation.isPending || isImportInProgress;

    const handleExport = (): void => {
        if (isOperationInProgress) {
            return;
        }
        exportMutation.mutate({ isBackup: false });
    };

    const handleImport = async (): Promise<void> => {
        if (!file || isOperationInProgress) {
            return;
        }
        backupDisclosure.open();
        try {
            await exportMutation.mutateAsync({ isBackup: true });
        } catch {
            return;
        } finally {
            backupDisclosure.close();
        }
        importMutation.mutate(file, {
            onSuccess: () => {
                setFile(null);
            }
        });
    };

    return (
        <>
            <Text fs="xs" fw={600}>
                Export
            </Text>
            <Box className="mb-2 text-sm text-gray-6">
                Export all projects to a `.zip` archive
            </Box>
            <Button
                data-testid="customer-projects-export-button"
                disabled={isOperationInProgress}
                loading={isBackingUp ? false : exportMutation.isPending}
                onClick={handleExport}
            >
                Export projects
            </Button>

            <Divider className="my-4" />

            <Text fs="sm" fw={600}>
                Import
            </Text>
            <Box className="mb-2 text-sm text-gray-6">
                Import all projects from a `.zip` archive. A backup of the
                current projects is downloaded before importing.
            </Box>
            <Box className="flex flex-col gap-4">
                <FileInput
                    accept=".zip"
                    disabled={isOperationInProgress}
                    fileInputProps={customerImportFileInputProps}
                    placeholder="Choose file (.zip)"
                    value={file}
                    onChange={setFile}
                />
                <Button
                    className="self-start"
                    data-testid="customer-projects-import-button"
                    disabled={!file || isOperationInProgress}
                    loading={isImportInProgress}
                    onClick={handleImport}
                >
                    Import projects
                </Button>
            </Box>
        </>
    );
};

export default CustomerImportExport;
